/**
 * Demo dataset for the CashFlow Statement module.
 * Covers 6 months of a fictional two-person household.
 * Shows gradual improvement in cashflow and passive income ratio.
 */

import type { MonthlySummary, Asset, Liability } from '../types/cashflow';

export const demoSummaries: MonthlySummary[] = [
  {
    month: '2025-10',
    earned_income: 7800,
    passive_income: 950,
    portfolio_income: 120,
    tax_expenses: 2200,
    housing_expenses: 1350,
    living_expenses: 1800,
    debt_expenses: 680,
    other_expenses: 320,
  },
  {
    month: '2025-11',
    earned_income: 7800,
    passive_income: 950,
    portfolio_income: 180,
    tax_expenses: 2200,
    housing_expenses: 1350,
    living_expenses: 1720,
    debt_expenses: 680,
    other_expenses: 250,
  },
  {
    month: '2025-12',
    earned_income: 7800,
    passive_income: 1100,
    portfolio_income: 220,
    tax_expenses: 2200,
    housing_expenses: 1350,
    living_expenses: 2100,
    debt_expenses: 680,
    other_expenses: 450,
  },
  {
    month: '2026-01',
    earned_income: 8200,
    passive_income: 1100,
    portfolio_income: 150,
    tax_expenses: 2350,
    housing_expenses: 1350,
    living_expenses: 1680,
    debt_expenses: 660,
    other_expenses: 200,
  },
  {
    month: '2026-02',
    earned_income: 8200,
    passive_income: 1200,
    portfolio_income: 200,
    tax_expenses: 2350,
    housing_expenses: 1350,
    living_expenses: 1650,
    debt_expenses: 660,
    other_expenses: 180,
  },
  {
    month: '2026-03',
    earned_income: 8200,
    passive_income: 1200,
    portfolio_income: 280,
    tax_expenses: 2350,
    housing_expenses: 1350,
    living_expenses: 1600,
    debt_expenses: 660,
    other_expenses: 210,
  },
];

export const demoAssets: Asset[] = [
  // Real estate — rental property generating passive income
  { month: '2025-10', name: 'Flat 2, Edinburgh', category: 'real_estate', current_value: 285000, monthly_cashflow: 850 },
  { month: '2025-11', name: 'Flat 2, Edinburgh', category: 'real_estate', current_value: 285000, monthly_cashflow: 850 },
  { month: '2025-12', name: 'Flat 2, Edinburgh', category: 'real_estate', current_value: 286000, monthly_cashflow: 1000 },
  { month: '2026-01', name: 'Flat 2, Edinburgh', category: 'real_estate', current_value: 286000, monthly_cashflow: 1000 },
  { month: '2026-02', name: 'Flat 2, Edinburgh', category: 'real_estate', current_value: 287500, monthly_cashflow: 1100 },
  { month: '2026-03', name: 'Flat 2, Edinburgh', category: 'real_estate', current_value: 287500, monthly_cashflow: 1100 },
  // Paper assets — stock portfolio generating dividends
  { month: '2025-10', name: 'Global Index Fund', category: 'paper', current_value: 24000, monthly_cashflow: 100 },
  { month: '2025-11', name: 'Global Index Fund', category: 'paper', current_value: 25200, monthly_cashflow: 100 },
  { month: '2025-12', name: 'Global Index Fund', category: 'paper', current_value: 26800, monthly_cashflow: 100 },
  { month: '2026-01', name: 'Global Index Fund', category: 'paper', current_value: 27400, monthly_cashflow: 100 },
  { month: '2026-02', name: 'Global Index Fund', category: 'paper', current_value: 28100, monthly_cashflow: 100 },
  { month: '2026-03', name: 'Global Index Fund', category: 'paper', current_value: 29200, monthly_cashflow: 100 },
  // Cash reserves — non-producing
  { month: '2025-10', name: 'Emergency Fund', category: 'cash', current_value: 12000, monthly_cashflow: 0 },
  { month: '2025-11', name: 'Emergency Fund', category: 'cash', current_value: 12500, monthly_cashflow: 0 },
  { month: '2025-12', name: 'Emergency Fund', category: 'cash', current_value: 13000, monthly_cashflow: 0 },
  { month: '2026-01', name: 'Emergency Fund', category: 'cash', current_value: 14000, monthly_cashflow: 0 },
  { month: '2026-02', name: 'Emergency Fund', category: 'cash', current_value: 15000, monthly_cashflow: 0 },
  { month: '2026-03', name: 'Emergency Fund', category: 'cash', current_value: 16200, monthly_cashflow: 0 },
];

export const demoLiabilities: Liability[] = [
  // Mortgage on rental property
  { month: '2025-10', name: 'Mortgage – Flat 2', category: 'mortgage', outstanding_balance: 218000, monthly_payment: 980, interest_rate: 4.5 },
  { month: '2025-11', name: 'Mortgage – Flat 2', category: 'mortgage', outstanding_balance: 217200, monthly_payment: 980, interest_rate: 4.5 },
  { month: '2025-12', name: 'Mortgage – Flat 2', category: 'mortgage', outstanding_balance: 216400, monthly_payment: 980, interest_rate: 4.5 },
  { month: '2026-01', name: 'Mortgage – Flat 2', category: 'mortgage', outstanding_balance: 215600, monthly_payment: 980, interest_rate: 4.5 },
  { month: '2026-02', name: 'Mortgage – Flat 2', category: 'mortgage', outstanding_balance: 214800, monthly_payment: 980, interest_rate: 4.5 },
  { month: '2026-03', name: 'Mortgage – Flat 2', category: 'mortgage', outstanding_balance: 214000, monthly_payment: 980, interest_rate: 4.5 },
  // Car loan
  { month: '2025-10', name: 'Car Loan', category: 'loan', outstanding_balance: 8400, monthly_payment: 350, interest_rate: 6.9 },
  { month: '2025-11', name: 'Car Loan', category: 'loan', outstanding_balance: 8050, monthly_payment: 350, interest_rate: 6.9 },
  { month: '2025-12', name: 'Car Loan', category: 'loan', outstanding_balance: 7700, monthly_payment: 350, interest_rate: 6.9 },
  { month: '2026-01', name: 'Car Loan', category: 'loan', outstanding_balance: 7350, monthly_payment: 350, interest_rate: 6.9 },
  { month: '2026-02', name: 'Car Loan', category: 'loan', outstanding_balance: 7000, monthly_payment: 350, interest_rate: 6.9 },
  { month: '2026-03', name: 'Car Loan', category: 'loan', outstanding_balance: 6650, monthly_payment: 350, interest_rate: 6.9 },
  // Credit card
  { month: '2025-10', name: 'Credit Card', category: 'credit_card', outstanding_balance: 2200, monthly_payment: 330, interest_rate: 19.9 },
  { month: '2025-11', name: 'Credit Card', category: 'credit_card', outstanding_balance: 1900, monthly_payment: 330, interest_rate: 19.9 },
  { month: '2025-12', name: 'Credit Card', category: 'credit_card', outstanding_balance: 2400, monthly_payment: 330, interest_rate: 19.9 },
  { month: '2026-01', name: 'Credit Card', category: 'credit_card', outstanding_balance: 2100, monthly_payment: 330, interest_rate: 19.9 },
  { month: '2026-02', name: 'Credit Card', category: 'credit_card', outstanding_balance: 1800, monthly_payment: 330, interest_rate: 19.9 },
  { month: '2026-03', name: 'Credit Card', category: 'credit_card', outstanding_balance: 1500, monthly_payment: 330, interest_rate: 19.9 },
];
